
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import { sharedState } from '../../../../../../framework/state';
import { getResource, postResource } from '../../../../../../framework/client/resource';
import TextField from '../../../../../../framework/fields/TextField.vue';
import SelectField from '../../../../../../framework/fields/SelectField.vue';
import { SalesInvoiceRow } from '../../../../../../../common/application/model/SalesInvoiceRow';
import { getDatabaseOptions } from '../../../../../../framework/service/options';
import SwitchField from '../../../../../../framework/fields/SwitchField.vue';
import NumberField from '../../../../../../framework/fields/NumberField.vue';
import IntegerField from '../../../../../../framework/fields/IntegerField.vue';
import DateField from '../../../../../../framework/fields/DateField.vue';
import PercentageField from '../../../../../../framework/fields/PercentageField.vue';
import CurrencyAmountField from '../../../../../../framework/fields/CurrencyAmountField.vue';
import { ValidationObserver } from 'vee-validate';
import AutoCompleteField from '../../../../../../framework/fields/AutoCompleteField.vue';
import VendorContractSelect from '../../../../../fields/VendorContractSelect.vue';
import { VendorContract } from '../../../../../../../common/application/model/VendorContract';
import { VehicleContract } from '../../../../../../../common/application/model/VehicleContract';
import { Account } from '../../../../../../../common/application/model/Account';
import { ApplicationResource } from '../../../../../../../common/application/enumeration/ApplicationResource';
import { updateCachedResourceRow } from '../../../../../service/query_cache_service';
import { errorToast, successToast } from '../../../../../service/toast_service';
import { debounce } from 'lodash';
import { calculateInvoiceRowSums } from '../../../../../service/helper_service';
import { getInvoiceRowTypeVatPercentage } from '../../../../../client/invoice_row_type_vat_percentage_client';

@Component({
    components: {
        DateField,
        PercentageField,
        NumberField,
        CurrencyAmountField,
        SelectField,
        TextField,
        SwitchField,
        IntegerField,
        ValidationObserver,
        AutoCompleteField,
        VendorContractSelect,
    },
})
export default class AddSalesInvoiceRow extends Vue {
    // Constants
    readonly resourceType = ApplicationResource.SALES_INVOICE_ROW;

    // Properties
    @Prop(String) readonly salesInvoiceId!: string;

    // State
    shared = sharedState;
    local = {
        row: {
            unit: 'kpl',
            unitPrice: 1,
            sumVat: 0,
            sumVatExcluded: 1,
            sumVatIncluded: 1,
            vatPercentage: 0,
            quantity: 1,
        } as SalesInvoiceRow,
        invoiceRowTypeOptions: [] as { id: string | undefined; label: string }[],
    };

    // Functions
    async mounted() {
        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);
        this.local.row = {
            ...this.local.row,
            salesInvoiceId: this.salesInvoiceId,
        };
    }

    @Watch('local.row.vehicleContractId')
    async vehicleContractIdChange(newValue: string) {
        if (newValue) {
            const vehicleContract = (await getResource<VehicleContract>(
                ApplicationResource.VEHICLE_CONTRACT,
                newValue,
            ))!!;
            this.local.row = {
                ...this.local.row,
                vehicleRegistrationNumber: vehicleContract.vehicleRegistrationNumber ?? '',
            };
        } else {
            this.local.row = {
                ...this.local.row,
                vehicleRegistrationNumber: '',
            };
        }
        await this.loadVatPercentage();
    }

    async loadVatPercentage() {
        const { invoiceRowType, vehicleContractId } = this.local.row;
        if (!invoiceRowType) {
            return;
        }
        const response = await getInvoiceRowTypeVatPercentage(invoiceRowType, 'fi', this.salesInvoiceId, vehicleContractId, this.local.row.startDate);
        if (response?.vatPercentage !== undefined) {
            this.local.row = {
                ...this.local.row,
                vatPercentage: response.vatPercentage
            };
            this.debounceSumCalculation();
        }
    }

    @Watch('local.row.invoiceRowType')
    async invoiceRowTypeChanged() {
        await this.loadVatPercentage();
    }

    @Watch('local.row.vendorContractId')
    async vendorContractIdChange(newValue: string) {
        if (newValue) {
            const vendorContract = (await getResource<VendorContract>(ApplicationResource.VENDOR_CONTRACT, newValue))!!;

            if (!vendorContract.customerExpenseAccountId) {
                return;
            }

            const customerExpenseAccount = (await getResource<Account>(
                ApplicationResource.ACCOUNT,
                vendorContract.customerExpenseAccountId,
            ))!!;
            this.local.row = {
                ...this.local.row,
                customerExpenseAccountCode: customerExpenseAccount.code,
            };
        } else {
            this.local.row = {
                ...this.local.row,
                customerExpenseAccountCode: '',
            };
        }
    }

    @Watch('local.row.quantity')
    async quantityChanged(quantity: number) {
        if (!quantity) {
            return;
        }

        this.debounceSumCalculation();
    }

    @Watch('local.row.vatPercentage')
    async vatPercentageChanged(vatPercentage: number) {
        if (!vatPercentage) {
            return;
        }

        this.debounceSumCalculation();
    }

    @Watch('local.row.unitPrice')
    async unitPriceChanged(unitPrice: number) {
        if (!unitPrice) {
            return;
        }

        this.debounceSumCalculation();
    }

    add() {
        this.addResource(true);
    }

    async addResource(goBack: boolean) {
        try {
            const newSalesInvoiceRow = await postResource<SalesInvoiceRow>(this.resourceType, this.local.row);
            successToast(this, 'message.salesInvoiceRowSaved');
            updateCachedResourceRow<SalesInvoiceRow>(newSalesInvoiceRow, newSalesInvoiceRow.id, this.resourceType);

            if (goBack) {
                this.back();
            }
        } catch (error) {
            errorToast(this);
        }
    }

    back() {
        this.$router.go(-1);
    }

    private debounceSumCalculation = debounce(() => {
        this.calculateRowSums();
    }, 500);

    private calculateRowSums() {
        const { sumVat, sumVatExcluded, sumVatIncluded } = calculateInvoiceRowSums(this.local.row);
        this.local.row = {
            ...this.local.row,
            sumVat,
            sumVatExcluded,
            sumVatIncluded,
        };
    }
}
